import { set } from 'lodash';

/** Split path "page.textName" into { page, textName }. */
export function pathToPageAndTextName(path) {
  const lastDot = path.lastIndexOf('.');
  if (lastDot === -1) return { page: '', textName: path };
  return { page: path.slice(0, lastDot), textName: path.slice(lastDot + 1) };
}

/** Unique row id for keyField. */
export function rowId(path, locale) {
  return `${path}\0${locale}`;
}

/** Flatten language object to rows [{ path, locale, value }, ...]. */
export function flattenLanguage(obj, prefix = '') {
  if (obj == null || typeof obj !== 'object') return [];
  return Object.entries(obj).flatMap(([key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof val === 'string') {
      return [{ path, locale: 'english', value: val }];
    }
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const values = Object.values(val);
      const allStrings = values.length > 0 && values.every((v) => typeof v === 'string');
      if (allStrings) {
        return Object.entries(val).map(([locale, value]) => ({ path, locale, value }));
      }
      return flattenLanguage(val, path);
    }
    return [];
  });
}

/** Build nested language object from rows: language[page][textName][locale] = value. */
export function unflattenRowsToLanguage(rows) {
  const result = {};
  rows.forEach(({ path, locale, value }) => {
    const { page, textName } = pathToPageAndTextName(path);
    if (!result[page]) result[page] = {};
    if (!result[page][textName]) result[page][textName] = {};
    result[page][textName][locale] = value;
  });
  return result;
}

export const METADATA_KEYS = ['path', 'page', 'textName'];

/** Convert flat rows to grouped format: [{ path, page, textName, english, french, ... }]. */
export function flatToGrouped(flatRows) {
  const byPath = {};
  flatRows.forEach(({
    path, locale, value, page, textName,
  }) => {
    const { page: p, textName: t } = pathToPageAndTextName(path);
    const meta = { page: page ?? p, textName: textName ?? t };
    if (!byPath[path]) byPath[path] = { path, ...meta };
    byPath[path][locale] = value;
  });
  return Object.values(byPath);
}

/** Convert grouped rows to flat for API: [{ path, locale, value }, ...]. */
export function groupedToFlat(groupedRows) {
  return groupedRows.flatMap((row) => {
    const { path } = row;
    return Object.entries(row)
      .filter(([key]) => !METADATA_KEYS.includes(key))
      .map(([locale, value]) => ({ path, locale, value }));
  });
}

/** Extract unique locale names from grouped language rows. */
export function getLocalesFromRows(languageRows) {
  const locales = new Set();
  languageRows.forEach((row) => {
    Object.keys(row)
      .filter((k) => !METADATA_KEYS.includes(k))
      .forEach((k) => locales.add(k));
  });
  return [...locales].sort((a, b) => a.localeCompare(b));
}

/** Apply locale order; appends any locales not in order, sorted. */
export function getAvailableLocales(localeOrder, localesFromRows) {
  if (localeOrder.length === 0) return localesFromRows;
  const ordered = localeOrder.filter((l) => localesFromRows.includes(l));
  const extra = localesFromRows
    .filter((l) => !localeOrder.includes(l))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...extra];
}

const PATH_COLUMN_NAMES = ['path', 'id', 'Key'];

/** Parse CSV data into merged grouped rows and changes map for import. */
export function parseCsvToLanguageImport(data, languageRows) {
  const allColumns = [...new Set(data.flatMap((row) => Object.keys(row)))];
  const pathColumn = allColumns.find((c) => PATH_COLUMN_NAMES.includes(c)) || allColumns[0];
  const fileLocales = allColumns.filter((c) => c !== pathColumn);

  const csvByPath = data.reduce((acc, row) => {
    const path = row[pathColumn];
    if (!path) return acc;
    const normalizedRow = { ...row };
    fileLocales.forEach((locale) => {
      if (normalizedRow[locale] === undefined || normalizedRow[locale] === null) {
        normalizedRow[locale] = '';
      }
    });
    acc[path] = normalizedRow;
    return acc;
  }, {});

  const existingByPath = languageRows.reduce((acc, row) => {
    acc[row.path] = row;
    return acc;
  }, {});

  const allPaths = [...new Set([...Object.keys(csvByPath), ...Object.keys(existingByPath)])];
  const changesMap = {};

  const mergedGrouped = allPaths.map((path) => {
    const csvRow = csvByPath[path];
    const existingRow = existingByPath[path];
    const { page, textName } = pathToPageAndTextName(path);
    const row = { path, page, textName };

    fileLocales.forEach((locale) => {
      const csvValue = csvRow?.[locale];
      const existingValue = existingRow?.[locale] ?? '';
      row[locale] = csvValue ?? existingValue ?? '';

      if (!changesMap[path]) changesMap[path] = {};
      if (csvValue !== undefined && csvValue !== null) {
        if (existingValue !== csvValue) changesMap[path][locale] = 'modified';
      } else if (!existingValue) {
        changesMap[path][locale] = 'empty';
      }
    });

    return row;
  });

  return { mergedGrouped, changesMap, fileLocales };
}

/** Convert grouped rows to nested language object for API: { path.locale: value }. */
export function groupedToLanguageObject(groupedRows) {
  const flat = groupedToFlat(groupedRows);
  const languages = {};
  flat.forEach(({ path, locale, value }) => {
    set(languages, `${path}.${locale}`, value);
  });
  return languages;
}
